import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Wrapper autour de useTranslation qui essaie plusieurs namespaces dans l'ordre
 * et tombe en fallback sur la clé si aucune traduction n'est trouvée.
 *
 * `t` est mémoïsé pour rester stable entre les renders, même quand le caller passe
 * un inline array (ex. useDynamicTranslation([NsKey.Contract, NsKey.Common])) — ça
 * évite les boucles infinies dans les useEffect/useCallback qui déclareraient `t`
 * en dépendance.
 *
 * `t` n'est recréé que lorsque :
 *  - la liste des namespaces (joint par '|') change,
 *  - ou la langue active de i18next change.
 */
export const useDynamicTranslation = (namespaces: string[]) => {
  const { i18n } = useTranslation();

  // Lit toujours la dernière valeur des namespaces sans déclarer le tableau lui-même
  // comme dépendance (qui changerait d'identité à chaque render si inline).
  const namespacesRef = useRef(namespaces);
  namespacesRef.current = namespaces;
  const namespacesKey = namespaces.join("|");

  const t = useCallback(
    (key: string, options?: Record<string, any>) => {
      for (const ns of namespacesRef.current) {
        const translation = i18n.t(key, {
          ns,
          defaultValue: key,
          returnNull: false,
          ...options,
        });
        if (translation !== key) return translation;
      }
      return key;
    },
    // i18n est stable (singleton i18next). i18n.language change avec la langue active
    // → on regénère t pour que les consommateurs qui le memoizent récupèrent la bonne
    // langue.
    [i18n, i18n.language, namespacesKey],
  );

  return useMemo(() => ({ t }), [t]);
};
